import { useEffect, useState } from "react";
import { Reorder, useDragControls } from "framer-motion";
import { GripVertical, ChevronUp, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { getExerciseOptions } from "@/data/defaultRoutine";

// Se reordena en una lista compacta aparte, no arrastrando las tarjetas: las
// tarjetas ocupan media pantalla y arrastrarlas obligaría a hacer scroll en
// mitad del gesto, que en móvil es un suplicio.

function Row({ exercise, index, total, onMove }) {
  const controls = useDragControls();

  const options = getExerciseOptions(exercise);
  const name = options ? options[0].name : exercise.name;
  const img = (options ? options[0].img : null) || exercise.img;

  return (
    <Reorder.Item
      value={exercise}
      dragListener={false}
      dragControls={controls}
      className="flex items-center gap-2.5 p-2 rounded-2xl bg-secondary/30 select-none"
    >
      <button
        // El arrastre solo empieza desde el asa: si escuchara en toda la fila,
        // no se podría hacer scroll de la lista con el dedo.
        onPointerDown={(e) => controls.start(e)}
        aria-label={`Arrastrar ${name}`}
        className="touch-none cursor-grab active:cursor-grabbing text-muted-foreground/50 hover:text-foreground transition-colors p-1"
      >
        <GripVertical className="h-4 w-4" />
      </button>

      <div className="w-10 h-10 rounded-lg overflow-hidden bg-secondary/50 flex-shrink-0">
        {img && <img src={img} alt="" className="w-full h-full object-contain" loading="lazy" />}
      </div>

      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-foreground truncate">{name}</p>
        <p className="text-[11px] text-muted-foreground truncate">{exercise.muscleGroup}</p>
      </div>

      {/* Flechas además del arrastre: es lo que funciona con teclado y lo que
          la gente encuentra sin tener que descubrir el gesto. */}
      <div className="flex flex-col flex-shrink-0">
        <button
          onClick={() => onMove(index, index - 1)}
          disabled={index === 0}
          aria-label={`Subir ${name}`}
          className="h-5 w-6 flex items-center justify-center text-muted-foreground/60 hover:text-foreground disabled:opacity-20 transition-colors"
        >
          <ChevronUp className="h-3.5 w-3.5" />
        </button>
        <button
          onClick={() => onMove(index, index + 1)}
          disabled={index === total - 1}
          aria-label={`Bajar ${name}`}
          className="h-5 w-6 flex items-center justify-center text-muted-foreground/60 hover:text-foreground disabled:opacity-20 transition-colors"
        >
          <ChevronDown className="h-3.5 w-3.5" />
        </button>
      </div>
    </Reorder.Item>
  );
}

export function ReorderExercisesDialog({ open, onOpenChange, exercises, onSave }) {
  const [items, setItems] = useState(exercises);

  // Se parte siempre del orden real al abrir, para que cancelar y volver a
  // entrar no arrastre una edición descartada.
  useEffect(() => {
    if (open) setItems(exercises);
  }, [open, exercises]);

  const move = (from, to) => {
    if (to < 0 || to >= items.length) return;
    const next = [...items];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    setItems(next);
  };

  const handleSave = () => {
    onSave(items.map((e) => e.id));
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass-surface rounded-3xl border-0 w-[calc(100vw-2rem)] max-w-md">
        <DialogHeader className="text-left">
          <DialogTitle className="text-foreground text-xl font-bold tracking-tight">
            Reordenar ejercicios
          </DialogTitle>
          <DialogDescription className="text-muted-foreground text-sm">
            Arrastra desde el asa o usa las flechas.
          </DialogDescription>
        </DialogHeader>

        <Reorder.Group
          axis="y"
          values={items}
          onReorder={setItems}
          className="space-y-1.5 max-h-[55vh] overflow-y-auto min-w-0"
        >
          {items.map((ex, i) => (
            <Row key={ex.id} exercise={ex} index={i} total={items.length} onMove={move} />
          ))}
        </Reorder.Group>

        <DialogFooter className="flex-row gap-2">
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            className="flex-1 rounded-xl glass-surface text-foreground/80"
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSave}
            className="flex-1 rounded-xl bg-primary text-primary-foreground font-bold"
          >
            Guardar orden
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
